import { APP_INITIALIZER, ApplicationConfig, DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { NZ_DATE_CONFIG, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import { CookieService } from 'ngx-cookie-service';

import { InternationalizationService } from '@services/utils/internationalization/internationalization.service';
import { SessionService } from '@services/utils/session.service';
import { MoneyPipe } from '@pipes/index';
import { WINDOW_PROVIDERS } from '@services/utils/window/window.provider';

import { environment } from '../environments/environment';
import { AuthInterceptor } from './core/auth-interceptor';
import { GlobalErrorHandler } from './core/global-error/global-error.service';
import { HttpLoaderFactory } from './core/http-loader.factory';
import { ModalBuilderService } from './services';
import { appRoutes } from './app.routes';
import { getCurrencyCode, getDateFns, getFirstDayOfWeek, getLanguage, getLocale } from './app.functions';

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            BrowserModule,
            FormsModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient]
                }
            }),
            StoreModule.forRoot({}, {})
        ),
        provideRouter(
            appRoutes,
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            withInMemoryScrolling({ scrollPositionRestoration: 'top' })
        ),
        provideAppInitializer(
            () => inject(InternationalizationService).importLocale()
        ),
        {
            provide: NZ_I18N,
            deps: [InternationalizationService],
            useFactory: getLocale
        },
        {
            provide: LOCALE_ID,
            deps: [InternationalizationService],
            useFactory: getLanguage
        },
        {
            provide: NZ_DATE_CONFIG,
            deps: [InternationalizationService],
            useFactory: getFirstDayOfWeek
        },
        {
            provide: NZ_DATE_LOCALE,
            deps: [InternationalizationService],
            useFactory: getDateFns
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            deps: [SessionService],
            useFactory: getCurrencyCode
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.recaptchaKey
        },
        CookieService,
        ModalBuilderService,
        CurrencyPipe,
        MoneyPipe,
        WINDOW_PROVIDERS,
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations()
    ]
};

